import React, { useState } from "react";
import "./App.css";

const canvasWidth = 1024;
const canvasHeight = 1024;
const borderThickness = 16;
const borderExcess = 1; // Add a tad more to ensure there's some overlap between adjacent elements to avoid gaps between adjacent <rect> elements
const backgroundFill = "#FBFCF4";
const borderFillColor = "#000000";

const fillColors = [
  '#FFF000',
  '#F70000',
  '#1A1414',
  '#004DE6',
  backgroundFill,
  backgroundFill,
  backgroundFill
];

function getRndFillColorIndex(): number {
  const maxIndex = fillColors.length - 1;
  const index = getRndInteger(0, maxIndex);
  return index;
}

function getNextFillColorIndex(index: number): number {
  const originalValue = fillColors[index];
  while (fillColors[index] === originalValue) {
    index = (index + 1) % fillColors.length;
  }

  return index;
}

const ratios = [0.2, 0.3333, 0.5, 0.6666, 0.8];

function getRndRatioIndex(): number {
  const maxIndex = ratios.length - 1;
  return getRndInteger(0, maxIndex);
}

function getNextRatioIndex(index: number): number {
  const originalValue = ratios[index];
  while (ratios[index] === originalValue) {
    index = (index + 1) % ratios.length;
  }

  return index;
}

function getRndInteger(min: number, max: number) {
  return Math.round(Math.random() * (max - min)) + min;
}

function MondrianRect(props: {
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  const [fillColorIndex, setFillColorIndex] = useState(getRndFillColorIndex());

  return (
    <rect
      x={props.x}
      y={props.y}
      width={props.width}
      height={props.height}
      fill={fillColors[fillColorIndex]}
      onClick={() => setFillColorIndex(getNextFillColorIndex(fillColorIndex))}
    />
  );
}

interface MondrianBlockProps {
  depth: number;
  x1: number;
  y1: number;
  width: number;
  height: number;
}
function MondrianBlock(props: MondrianBlockProps): {
  bottomLayer: JSX.Element[];
  topLayer: JSX.Element[];
} {
  const [borderDirection, setBorderDirection] = useState(getRndInteger(0, 1) === 0 ? "vertical" : "horizontal")
  const [ratioIndex, setRatioIndex] = useState(getRndRatioIndex());

  if (props.depth === 1) {
    // just one rect as fill color
    return {
      bottomLayer: [
        <MondrianRect
          x={props.x1 - borderExcess}
          y={props.y1 - borderExcess}
          width={props.width + borderExcess * 2}
          height={props.height + borderExcess * 2}
        />,
      ],
      topLayer: [],
    };
  }

  // split into two blocks, recursively
  const ratio = ratios[ratioIndex];
  let firstBlockX1: number;
  let firstBlockY1: number;
  let firstChildBlockWidth: number;
  let firstChildBlockHeight: number;

  let borderX1: number;
  let borderY1: number;
  let borderWidth: number;
  let borderHeight: number;

  let secondBlockX1: number;
  let secondBlockY1: number;
  let secondChildBlockWidth: number;
  let secondChildBlockHeight: number;

  if (borderDirection === "horizontal") {
    firstBlockX1 = props.x1;
    firstBlockY1 = props.y1;
    firstChildBlockWidth = props.width;
    firstChildBlockHeight = (props.height - borderThickness) * ratio;

    borderX1 = props.x1 - borderExcess;
    borderY1 = props.y1 + firstChildBlockHeight;
    borderWidth = props.width + 2 * borderExcess;
    borderHeight = borderThickness;

    secondBlockX1 = props.x1;
    secondBlockY1 = borderY1 + borderHeight;
    secondChildBlockWidth = props.width;
    secondChildBlockHeight = (props.height - borderThickness) * (1.0 - ratio);
  } else {
    firstBlockX1 = props.x1;
    firstBlockY1 = props.y1;
    firstChildBlockWidth = (props.width - borderThickness) * ratio;
    firstChildBlockHeight = props.height;

    borderX1 = props.x1 + firstChildBlockWidth;
    borderY1 = props.y1 - borderExcess;
    borderWidth = borderThickness;
    borderHeight = props.height + borderExcess * 2;

    secondBlockX1 = borderX1 + borderThickness;
    secondBlockY1 = props.y1;
    secondChildBlockWidth = (props.width - borderThickness) * (1.0 - ratio);
    secondChildBlockHeight = props.height;
  }

  const firstBlockLayers = MondrianBlock({
    depth: props.depth - 1,
    x1: firstBlockX1,
    y1: firstBlockY1,
    width: firstChildBlockWidth,
    height: firstChildBlockHeight,
  });

  const secondBlockLayers = MondrianBlock({
    depth: props.depth - 1,
    x1: secondBlockX1,
    y1: secondBlockY1,
    width: secondChildBlockWidth,
    height: secondChildBlockHeight,
  });

  return {
    bottomLayer: [
      ...firstBlockLayers.bottomLayer,
      ...secondBlockLayers.bottomLayer,
    ],
    topLayer: [
      ...firstBlockLayers.topLayer,
      ...secondBlockLayers.topLayer,
      <rect
        x={borderX1}
        y={borderY1}
        width={borderWidth}
        height={borderHeight}
        fill={borderFillColor}
        onClick={() => setRatioIndex(getNextRatioIndex(ratioIndex))}
        onContextMenu={(evt) => { setBorderDirection(borderDirection === 'horizontal' ? 'vertical' : 'horizontal'); evt.preventDefault(); return false; }}
      />,
    ],
  };
}

interface MondrianSvgProps {
  width: number;
  height: number;
}
function MondrianSvg(props: MondrianSvgProps) {
  const { width, height } = props;
  const layers = MondrianBlock({
    x1: 0,
    y1: 0,
    width: width,
    height: height,
    depth: 4,
  });

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${width} ${height}`}
    >
      <rect width={width} height={height} fill={backgroundFill} />
      {layers.bottomLayer}
      {layers.topLayer}
    </svg>
  );
}

// Function to download data to a file
function download(data: string, filename: string, type: string) {
  var file = new Blob([data], { type: type });

  const a = document.createElement("a");
  const url = URL.createObjectURL(file);
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(function () {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 0);
}

function onSaveSvg() {
  const svg = document.querySelector("svg")!;
  download(svg.outerHTML, "mondrian.svg", "text/plain");
}

function onRefresh() {
  window.location.reload();
}

function App() {
  return (
    <div className="container">
      <MondrianSvg width={canvasWidth} height={canvasHeight} />

      <div className="controls">
        <button className="refresh" onClick={onRefresh}>Refresh</button>
        <button className="save" onClick={onSaveSvg}>Save SVG</button>
      </div>
    </div>
  );
}

export default App;
